import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../axiosInterceptor';

export interface InviteCoachPayload {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  area_of_expertise: string;
  notes: string;
}

export interface Coach {
  id: string;
  user_id: string;
  name: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  avatar?: string;
  bio?: string;
  zoom_account_linked?: boolean;
  commission_rate?: number;
  approved?: boolean;
  location?: string;
  timezone?: string;
  created_at?: string;
  updated_at?: string;
  completed_sessions: number;
  area_of_expertise?: string;
  notes?: string;
  last_session?: string | null;
}

export interface InviteCoachResponse {
  type: "new_user" | "existing_user";
  user_id: string;
  email: string;
  role?: string;
  status: string;
  invitation_token?: string;
  expires_at?: string;
  temporary_password?: string;
  message: string;
}

interface CoachState {
  coaches: Coach[];
  loading: boolean;
  error: string | null;
  inviteSuccess: boolean;
  lastInviteResponse: InviteCoachResponse | null;
}

const initialState: CoachState = {
  coaches: [],
  loading: false,
  error: null,
  inviteSuccess: false,
  lastInviteResponse: null,
};

// Async thunk for inviting a coach
export const inviteCoach = createAsyncThunk(
  'coach/invite',
  async (payload: InviteCoachPayload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/admin/coach/invite', payload);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to invite coach');
    }
  }
);

// Async thunk for fetching all coaches (both active and pending)
export const fetchCoaches = createAsyncThunk(
  'coach/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch all coaches (active and pending)
      const { data } = await axios.get('/api/coaches');
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch coaches');
    }
  }
);

const coachSlice = createSlice({
  name: 'coach',
  initialState,
  reducers: {
    resetInviteSuccess: (state) => {
      state.inviteSuccess = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCoach: (state) => {
      state.loading = false;
      state.coaches = [];
      state.error = null;
      state.inviteSuccess = false;
    },
  },
  extraReducers: (builder) => {
    // Invite coach
    builder
      .addCase(inviteCoach.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.inviteSuccess = false;
        state.lastInviteResponse = null;
      })
      .addCase(inviteCoach.fulfilled, (state, action: PayloadAction<InviteCoachResponse>) => {
        state.loading = false;
        state.inviteSuccess = true;
        state.lastInviteResponse = action.payload;
      })
      .addCase(inviteCoach.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.inviteSuccess = false;
        state.lastInviteResponse = null;
      });

    // Fetch coaches
    builder
      .addCase(fetchCoaches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoaches.fulfilled, (state, action: PayloadAction<Coach[]>) => {
        state.loading = false;
        state.coaches = action.payload;
      })
      .addCase(fetchCoaches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetInviteSuccess, clearError, resetCoach } = coachSlice.actions;
export default coachSlice.reducer;
